// frontend/src/components/ReportModal.js
import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import api from '../services/api';

const MOTIVOS_ITEM = [
    'Producto inexistente o falso',
    'Precio engañoso',
    'Contenido inapropiado',
    'Spam o publicidad',
    'Producto prohibido',
    'Otro',
];

const MOTIVOS_USUARIO = [
    'Comportamiento abusivo',
    'Estafa o fraude',
    'Suplantación de identidad',
    'Spam',
    'Otro',
];

/**
 * Modal de reporte reutilizable.
 * 
 * Props:
 *   show        - boolean
 *   onHide      - función para cerrar
 *   itemId      - id del item a reportar (opcional)
 *   usuarioId   - id del usuario a reportar (opcional)
 *   nombre      - nombre del item o usuario (para mostrar en el título)
 */
function ReportModal({ show, onHide, itemId, usuarioId, nombre }) {
    const [motivo, setMotivo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const [error, setError] = useState('');

    const esItem = !!itemId;
    const motivos = esItem ? MOTIVOS_ITEM : MOTIVOS_USUARIO;

    const handleClose = () => {
        // Resetear estado al cerrar
        setMotivo('');
        setDescripcion('');
        setEnviando(false);
        setEnviado(false);
        setError('');
        onHide();
    };

    const handleEnviar = async () => {
        if (!motivo) { setError('Selecciona un motivo.'); return; }
        setEnviando(true);
        setError('');
        try {
            await api.post('/reportes/', {
                item_id: itemId || null,
                usuario_reportado_id: usuarioId || null,
                motivo,
                descripcion: descripcion.trim() || null
            });
            setEnviado(true);
        } catch (e) {
            setError(e.response?.data?.detail || 'Error al enviar el reporte.');
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered size="sm">
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1rem' }}>
                    <i className="bi bi-flag me-2 text-danger"></i>
                    Reportar {esItem ? 'publicación' : 'usuario'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {enviado ? (
                    <div className="text-center py-3">
                        <i className="bi bi-check-circle-fill text-success fs-1 d-block mb-3"></i>
                        <p className="mb-1 fw-semibold">Reporte enviado</p>
                        <small className="text-muted">Vamos a revisarlo pronto. Gracias por ayudarnos a mantener la comunidad.</small>
                    </div>
                ) : (
                    <>
                        {nombre && (
                            <p className="text-muted small mb-3">
                                Estás reportando: <strong>{nombre}</strong>
                            </p>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-semibold">Motivo</Form.Label>
                            {motivos.map(m => (
                                <Form.Check
                                    key={m}
                                    type="radio"
                                    id={`motivo-${m}`}
                                    label={m}
                                    value={m}
                                    checked={motivo === m}
                                    onChange={e => setMotivo(e.target.value)}
                                    className="mb-1"
                                />
                            ))}
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label className="small fw-semibold">Descripción adicional <span className="text-muted fw-normal">(opcional)</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Contanos más detalles..."
                                value={descripcion}
                                onChange={e => setDescripcion(e.target.value)}
                                maxLength={300}
                            />
                        </Form.Group>
                        {error && <div className="alert alert-danger py-2 small">{error}</div>}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer className="py-2">
                {enviado ? (
                    <Button variant="secondary" size="sm" onClick={handleClose}>Cerrar</Button>
                ) : (
                    <>
                        <Button variant="secondary" size="sm" onClick={handleClose}>Cancelar</Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleEnviar}
                            disabled={enviando || !motivo}
                        >
                            {enviando ? 'Enviando...' : 'Enviar reporte'}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}

export default ReportModal;
